import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  CLINICAL_SNIPPETS,
  groupSnippetsByCategory,
  searchSnippets,
} from '../data/clinicalSnippets.js'
import { createMySnippet, listMySnippets } from '../api/snippets.js'

/**
 * SnippetPicker — drop next to any long-form clinical text field.
 *
 * Renders a small "💡 Snippets" button. On click, opens a popover with
 * a search box at the top + categorised list of matching snippets.
 * Clicking a snippet calls `onInsert(text)` so the parent can decide
 * how to splice it into the textarea (append, replace, or insert at
 * cursor — typical pattern is `value + (value ? '\n' : '') + text`).
 *
 * The pool is the union of:
 *   - CLINICAL_SNIPPETS (web/src/data/clinicalSnippets.js) — the
 *     curated PNG-tuned system library
 *   - the caller's own personal snippets fetched from
 *     /api/doctors/my-snippets/?section_code=<filter.section>
 * Personal snippets are rendered FIRST under a "My Snippets" group so
 * the clinician sees their own templates above the system catalogue.
 *
 * Add-new flow:
 *   The popover footer has a "+ Add a snippet" button. Click expands an
 *   inline form (title + body); save POSTs to the API, invalidates the
 *   list query, and the new snippet appears at the top immediately.
 *
 * Design intent: zero-keystroke UX for the common case (open, scan,
 * pick) but search-as-you-type for power users who know the snippet
 * by name. Keyboard: Esc closes, ↑/↓ navigates results, Enter inserts
 * the highlighted snippet.
 *
 * Props:
 *   onInsert(text)  required — called with the snippet body string
 *   buttonClassName  optional — tweak the button styling
 *   buttonLabel      optional — defaults to "💡 Snippets"
 *   filter           optional — scope which snippets are shown:
 *                      { section: 'L' }    → only system snippets whose
 *                                            `sections` array contains 'L',
 *                                            AND only personal snippets
 *                                            saved against section_code='L'.
 *                                            Add-new form pre-selects this
 *                                            section so the clinician doesn't
 *                                            have to pick it.
 *                      { category: 'X' }   → legacy: scope to one category.
 *                                            Personal snippets are skipped
 *                                            when in legacy mode.
 */
export default function SnippetPicker({
  onInsert,
  buttonClassName = '',
  buttonLabel = '💡 Snippets',
  filter = null,
}) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [activeIdx, setActiveIdx] = useState(0)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newBody, setNewBody] = useState('')
  const [addError, setAddError] = useState(null)
  const popRef = useRef(null)
  const buttonRef = useRef(null)

  // Section-scoped query. Skips entirely when filter is category-only,
  // because personal snippets are sectioned, not categorised.
  const sectionFilter = filter?.section || null
  const userSnippetsQ = useQuery({
    queryKey: ['my-snippets', sectionFilter],
    queryFn: () => listMySnippets({ section: sectionFilter }),
    enabled: open && Boolean(sectionFilter),
    staleTime: 60_000,
  })

  // Normalise personal snippets to the system-snippet shape so they
  // flow through the same render path. The "trigger" surrogate uses
  // a "my:" prefix to avoid colliding with system snippet trigger names.
  const userSnippets = useMemo(() => {
    const raw = userSnippetsQ.data || []
    return raw.map((s) => ({
      trigger: `my:${s.id}:${s.title}`,
      title: s.title,
      aliases: [],
      category: s.category || 'My Snippets',
      sections: [s.section_code],
      body: s.body,
      _isPersonal: true,
    }))
  }, [userSnippetsQ.data])

  // Filter pool: scope by section letter (preferred) or category (legacy),
  // then by free-text query in the second pass below. Personal snippets
  // are prepended so they appear first in the popover.
  const pool = useMemo(() => {
    let systemPool = CLINICAL_SNIPPETS
    if (filter?.section) {
      systemPool = systemPool.filter(
        (s) => Array.isArray(s.sections) && s.sections.includes(filter.section),
      )
    } else if (filter?.category) {
      systemPool = systemPool.filter((s) => s.category === filter.category)
    }
    return [...userSnippets, ...systemPool]
  }, [filter?.section, filter?.category, userSnippets])

  const matches = useMemo(() => searchSnippets(q, pool), [q, pool])
  const grouped = useMemo(() => groupSnippetsByCategory(matches), [matches])

  // Mutation: create a new personal snippet for the current section.
  const createMut = useMutation({
    mutationFn: (payload) => createMySnippet(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-snippets', sectionFilter] })
      setShowAddForm(false)
      setNewTitle('')
      setNewBody('')
      setAddError(null)
    },
    onError: (err) => {
      const data = err?.response?.data
      const msg =
        data?.detail
        || data?.title?.[0]
        || data?.body?.[0]
        || data?.section_code?.[0]
        || err?.message
        || 'Failed to save snippet.'
      setAddError(typeof msg === 'string' ? msg : JSON.stringify(msg))
    },
  })

  // Click-outside dismiss + Esc.
  useEffect(() => {
    if (!open) return
    function onDocClick(e) {
      if (
        popRef.current && !popRef.current.contains(e.target)
        && buttonRef.current && !buttonRef.current.contains(e.target)
      ) {
        setOpen(false)
        setShowAddForm(false)
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') {
        if (showAddForm) {
          setShowAddForm(false)
        } else {
          setOpen(false)
        }
      }
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, showAddForm])

  // Reset highlight when matches change.
  useEffect(() => {
    setActiveIdx(0)
  }, [q, pool])

  function pick(snippet) {
    onInsert(snippet.body)
    setOpen(false)
    setQ('')
    setShowAddForm(false)
  }

  function onKeyDownSearch(e) {
    if (matches.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => Math.min(i + 1, matches.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      pick(matches[activeIdx])
    }
  }

  function onSaveNew(e) {
    e.preventDefault()
    setAddError(null)
    if (!sectionFilter) {
      setAddError('This picker has no section set; cannot save.')
      return
    }
    const title = newTitle.trim()
    const body = newBody.trim()
    if (!title) return setAddError('Title is required.')
    if (!body) return setAddError('Snippet body cannot be empty.')
    createMut.mutate({
      title,
      body,
      section_code: sectionFilter,
      category: 'My Snippets',
    })
  }

  return (
    <div className="relative inline-block">
      <button
        type="button"
        ref={buttonRef}
        onClick={() => setOpen((o) => !o)}
        className={
          'text-[11px] font-semibold border border-wantok-200 bg-wantok-50/60 text-wantok-800 ' +
          'hover:bg-wantok-100 rounded-md px-2 py-1 ' + buttonClassName
        }
        title="Insert a clinical text snippet"
      >
        {buttonLabel}
      </button>

      {open && (
        <div
          ref={popRef}
          className={
            'absolute z-50 right-0 mt-1 w-[440px] max-w-[90vw] ' +
            'bg-white border border-slate-200 rounded-lg shadow-lg ' +
            'overflow-hidden'
          }
        >
          <div className="p-2 border-b border-slate-100 bg-slate-50/60">
            <input
              type="search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={onKeyDownSearch}
              placeholder="Type to search snippets — e.g. 'triple', 'malaria', 'htn'…"
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:border-wantok-600 focus:outline-none focus:ring-1 focus:ring-wantok-600"
            />
            <div className="text-[10px] text-slate-400 mt-1 flex flex-wrap gap-x-2">
              <span>
                {matches.length} snippet{matches.length === 1 ? '' : 's'}
                {sectionFilter ? ` for Section ${sectionFilter}` : ''}
              </span>
              <span>·</span>
              <span>↑/↓ navigate · Enter insert · Esc close</span>
              {userSnippetsQ.isLoading && <span className="italic">Loading personal…</span>}
            </div>
          </div>

          <div className="max-h-[50vh] overflow-y-auto p-1">
            {matches.length === 0 ? (
              <div className="text-xs text-slate-400 italic p-3 text-center">
                {q ? `No snippets match "${q}".` : 'No snippets in this section yet.'}
              </div>
            ) : (
              grouped.map(({ category, items }) => (
                <div key={category} className="mb-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 px-2 pt-2 pb-1 flex items-center gap-1.5">
                    <span>{category}</span>
                    {category === 'My Snippets' && (
                      <span className="text-[9px] text-wantok-700 normal-case tracking-normal font-medium">
                        (yours)
                      </span>
                    )}
                  </div>
                  {items.map((s) => {
                    const flatIdx = matches.indexOf(s)
                    const isActive = flatIdx === activeIdx
                    return (
                      <button
                        key={s.trigger}
                        type="button"
                        onClick={() => pick(s)}
                        onMouseEnter={() => setActiveIdx(flatIdx)}
                        className={
                          'w-full text-left rounded px-2 py-1.5 transition-colors ' +
                          (isActive
                            ? 'bg-wantok-50 text-wantok-900'
                            : 'hover:bg-slate-50 text-slate-800')
                        }
                      >
                        <div className="text-xs font-semibold flex items-center gap-1.5">
                          {s.title || s.trigger}
                          {s._isPersonal && (
                            <span className="text-[9px] uppercase tracking-wider text-wantok-600 bg-wantok-50 border border-wantok-200 rounded px-1">
                              mine
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 leading-snug mt-0.5 line-clamp-2">
                          {s.body}
                        </div>
                      </button>
                    )
                  })}
                </div>
              ))
            )}
          </div>

          {/* Add-new footer — only available when a section filter is set,
              since personal snippets must be saved against a section. */}
          {sectionFilter && (
            <div className="border-t border-slate-100 bg-slate-50/60 p-2">
              {!showAddForm ? (
                <button
                  type="button"
                  onClick={() => setShowAddForm(true)}
                  className="text-[11px] font-semibold text-wantok-700 hover:text-wantok-900 hover:underline"
                >
                  + Add a snippet to Section {sectionFilter}
                </button>
              ) : (
                <form onSubmit={onSaveNew} className="space-y-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    New personal snippet · Section {sectionFilter}
                  </div>
                  <input
                    type="text"
                    autoFocus
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="Title — e.g. 'My empirical sepsis protocol'"
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:border-wantok-600 focus:outline-none focus:ring-1 focus:ring-wantok-600"
                  />
                  <textarea
                    rows={4}
                    value={newBody}
                    onChange={(e) => setNewBody(e.target.value)}
                    placeholder="Snippet body — the text that will be inserted into the field when picked."
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm focus:border-wantok-600 focus:outline-none focus:ring-1 focus:ring-wantok-600"
                  />
                  {addError && (
                    <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                      {addError}
                    </div>
                  )}
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddForm(false)
                        setAddError(null)
                      }}
                      className="text-[11px] font-medium text-slate-600 hover:text-slate-900 px-2 py-1"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createMut.isPending}
                      className="text-[11px] font-semibold bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-1 disabled:opacity-60"
                    >
                      {createMut.isPending ? 'Saving…' : 'Save snippet'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
